// Angular Import
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

// project import
import { ModulesService } from 'src/app/services/modules/modules-service';
import { SharedModule } from '../shared/shared-module';

@Component({
  selector: 'app-breadcrumb',
  imports: [CommonModule, RouterModule, SharedModule],
  templateUrl: './breadcrumbs.html',
  styleUrls: ['./breadcrumbs.scss']
})
export class Breadcrumbs {
  private route = inject(Router);
  private titleService = inject(Title);
  private moduleService = inject(ModulesService);

  breadcrumbs: any[] = [];
  categoryOptions: any[] = [];

  ngOnInit() {
    const initialRoute = this.route.url.replace(/^\/+/, '').split('?')[0]; // remove leading slash and query params
    this.updateBreadcrumbs(initialRoute);

    this.route.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const route = event.urlAfterRedirects.replace(/^\/+/, '').split('?')[0]; // remove leading slash and query params
        this.updateBreadcrumbs(route);
      }
    });
  }

  private updateBreadcrumbs(route: string | undefined) {
    if (!route) return;

    const modules = this.moduleService.getModules();

    if (!modules() || modules().length === 0) {
      setTimeout(() => this.updateBreadcrumbs(route), 0);
      return;
    }

    const { breadcrumbs, category } = this.moduleService.getBreadcrumbsByRoute(route);

    // Check route segments
    const routeSegments = route.split('/');
    const lastSegment = routeSegments[routeSegments.length - 1];
    const secondLastSegment = routeSegments.length > 1 ? routeSegments[routeSegments.length - 2] : '';

    const hasAddSegment = lastSegment.toLowerCase() === 'add';
    const hasOnboardSegment = lastSegment.toLowerCase() === 'onboard';
    const hasBulkUpdatePriceSegment = lastSegment.toLowerCase() === 'bulk-update-price';

    const hasEditSegment = secondLastSegment.toLowerCase() === 'edit' && !isNaN(Number(lastSegment));
    const containsDetails = route.toLowerCase().includes('/details');

    const dynamicBreadcrumbs: any[] = breadcrumbs.map((b) => ({ ...b }));
    if (category && category.submodules && dynamicBreadcrumbs.length > 0) {
      dynamicBreadcrumbs[dynamicBreadcrumbs.length - 1].submodules = category.submodules;
    }

    if (hasEditSegment && !dynamicBreadcrumbs.some((b) => b.name.toLowerCase() === 'edit')) {
      dynamicBreadcrumbs.push({ name: 'EDIT', route: route });
    } else if (containsDetails && !dynamicBreadcrumbs.some((b) => b.name.toLowerCase() === 'details')) {
      dynamicBreadcrumbs.push({ name: 'DETAILS', route: route });
    } else if (hasAddSegment && !dynamicBreadcrumbs.some((b) => b.name.toLowerCase() === 'add')) {
      dynamicBreadcrumbs.push({ name: 'ADD', route: route });
    } else if (hasOnboardSegment && !dynamicBreadcrumbs.some((b) => b.name.toLowerCase() === 'onboard')) {
      dynamicBreadcrumbs.push({ name: 'ONBOARD', route: route });
    } else if (hasBulkUpdatePriceSegment && !dynamicBreadcrumbs.some((b) => b.name.toLowerCase() === 'bulk-update-price')) {
      dynamicBreadcrumbs.push({ name: 'BULK_UPDATE_PRICE', route: route });
    }

    this.breadcrumbs = dynamicBreadcrumbs;
    this.categoryOptions = category?.submodules || [];
  }

  onCategoryChange(event: any) {
    const route = event.value || event.moduleRoute;
    if (route) this.route.navigate(['/', route]);
  }

  getBreadcrumbLink(route: string): any[] {
    if (!route) return ['/'];
    const cleanRoute = route.split('?')[0]; // remove query params before splitting route segments
    return ['/', ...cleanRoute.split('/')];
  }
}
